// 排行榜用的每人統計（scoreStats/{uid}）：存成績時累加，排行榜「全部期間」只讀每人一筆
// 純函式、不引用其他模組（scripts/rebuild-score-stats.mjs 也直接使用這個檔案）
export type Agg = {
  n: number;            // 測驗次數
  sumPct: number;       // 正確率加總（平均正確率）
  q: number;            // 作答題數
  correct: number;      // 答對題數
  fastQ: number;        // 正確率 80% 以上的測驗：題數
  fastTime: number;     //                       ：秒數
  best: { pct: number; total: number; score: number } | null;   // 最佳一次（題數達門檻才列入）
  last: number[];       // 最近 10 次正確率（舊到新）
  days: string[];       // 最近 60 個有測驗的日期（舊到新）
};
export type StatDoc = { uid?: string; nickname?: string; avatarUrl?: string | null; all: Agg; subs: Record<string, Agg> };
export type StatRec = { sub: string; pct: number; score: number; total: number; timeTaken?: number; ms: number };
export type StatRow = { uid: string; nickname: string; avatarUrl?: string | null; value: number; label: string; sub: string };
export type StatKind = 'best' | 'avg' | 'fast' | 'volume' | 'correct' | 'streak' | 'progress';

export const LAST_N = 10;
export const DAYS_N = 60;

export const dayKey = (ms: number) => { const d = new Date(ms); return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`; };

// 目前連續有測驗的天數；今天還沒練不算中斷（從昨天往回數）
export function streakOfDays(days: Set<string>, now = Date.now()) {
  let n = 0;
  const d = new Date(now);
  if (!days.has(dayKey(d.getTime()))) d.setDate(d.getDate() - 1);
  while (days.has(dayKey(d.getTime()))) { n++; d.setDate(d.getDate() - 1); }
  return n;
}

export const emptyAgg = (): Agg => ({ n: 0, sumPct: 0, q: 0, correct: 0, fastQ: 0, fastTime: 0, best: null, last: [], days: [] });

// 累加一筆成績（需依時間先後呼叫）
export function addToAgg(prev: Agg | undefined, r: StatRec, minQ: number): Agg {
  const a = prev ?? emptyAgg();
  const fast = r.pct >= 80 && r.total > 0 && !!r.timeTaken;
  const b = a.best;
  const better = r.total >= minQ && (!b || r.pct > b.pct || (r.pct === b.pct && r.total > b.total));
  const day = dayKey(r.ms);
  return {
    n: a.n + 1,
    sumPct: a.sumPct + r.pct,
    q: a.q + r.total,
    correct: a.correct + r.score,
    fastQ: a.fastQ + (fast ? r.total : 0),
    fastTime: a.fastTime + (fast ? r.timeTaken || 0 : 0),
    best: better ? { pct: r.pct, total: r.total, score: r.score } : b,
    last: a.last.concat(r.pct).slice(-LAST_N),
    days: (a.days.includes(day) ? a.days : a.days.concat(day)).slice(-DAYS_N),
  };
}

export function addToStat(prev: Partial<StatDoc> | undefined, r: StatRec, minQ: number, who: { nickname?: string; avatarUrl?: string | null }): StatDoc {
  const subs = Object.assign({}, prev?.subs);
  subs[r.sub] = addToAgg(subs[r.sub], r, minQ);
  return { nickname: who.nickname || '匿名', avatarUrl: who.avatarUrl ?? null, all: addToAgg(prev?.all, r, minQ), subs };
}

// 排名（與 leaderboardRank.rankScores 相同的規則）；sub 空字串為全部科目
export function rankStats(kind: StatKind, stats: StatDoc[], sub: string, minQ: number, now = Date.now()): StatRow[] {
  const out: StatRow[] = [];
  stats.forEach(s => {
    const a = sub ? s.subs?.[sub] : s.all;
    if (!a || !a.n || !s.uid) return;
    const who = { uid: s.uid, nickname: s.nickname || '匿名', avatarUrl: s.avatarUrl };
    if (kind === 'best') {
      if (a.best) out.push({ ...who, value: a.best.pct * 1000 + a.best.total, label: `${a.best.pct}%`, sub: `${a.best.score}/${a.best.total} 題` });
    } else if (kind === 'avg') {
      if (a.n < 5) return;
      const v = a.sumPct / a.n;
      out.push({ ...who, value: v, label: `${Math.round(v)}%`, sub: `${a.n} 次測驗` });
    } else if (kind === 'fast') {
      if (a.fastQ < minQ || !a.fastQ) return;
      const v = a.fastTime / a.fastQ;
      out.push({ ...who, value: -v, label: `${v.toFixed(1)} 秒/題`, sub: `${a.fastQ} 題` });
    } else if (kind === 'volume') {
      out.push({ ...who, value: a.q, label: `${a.q} 題`, sub: `${a.n} 次測驗` });
    } else if (kind === 'correct') {
      out.push({ ...who, value: a.correct, label: `${a.correct} 題`, sub: `正確率 ${Math.round(a.correct / Math.max(1, a.q) * 100)}%` });
    } else if (kind === 'streak') {
      const v = streakOfDays(new Set(a.days), now);
      if (v > 0) out.push({ ...who, value: v, label: `${v} 天`, sub: `${a.n} 次測驗` });
    } else if (kind === 'progress') {
      if (a.last.length < 10) return;
      const avg = (l: number[]) => l.reduce((x, y) => x + y, 0) / 5;
      const p = avg(a.last.slice(-10, -5)), c = avg(a.last.slice(-5));
      if (c - p > 0) out.push({ ...who, value: c - p, label: `+${(c - p).toFixed(1)}%`, sub: `${Math.round(p)}% → ${Math.round(c)}%` });
    }
  });
  return out.sort((x, y) => y.value - x.value);
}
